import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

interface CreateListingModalProps {
  portfolios: any[];
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateListingModal({ portfolios, open, onClose, onCreated }: CreateListingModalProps) {
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [askingPrice, setAskingPrice] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedPortfolio) {
      toast.error('Please select a portfolio');
      return;
    }
    if (!askingPrice || parseFloat(askingPrice) <= 0) {
      toast.error('Please enter a valid asking price');
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Call edge function to create listing
      toast.success('Listing created successfully');
      onCreated();
      onClose();
      setSelectedPortfolio('');
      setAskingPrice('');
      setDescription('');
    } catch (error) {
      toast.error('Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Listing</DialogTitle>
          <DialogDescription>
            List a portfolio on the marketplace for buyers to view and make offers
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="portfolio">Portfolio *</Label>
            <Select value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
              <SelectTrigger id="portfolio">
                <SelectValue placeholder="Select a portfolio" />
              </SelectTrigger>
              <SelectContent>
                {portfolios.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">Asking Price *</Label>
            <Input
              id="price"
              type="number"
              placeholder="Enter asking price"
              value={askingPrice}
              onChange={(e) => setAskingPrice(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="desc">Description (Optional)</Label>
            <Textarea
              id="desc"
              placeholder="Describe your portfolio, highlight key metrics..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="flex-1">
              {submitting ? 'Creating...' : 'Create Listing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
